import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authCapabilitiesSchema } from '@bolao/shared';
import { authClient } from '../auth-client.js';
import GoogleButton from './GoogleButton.js';
import styles from './GuestUpgradePanel.module.css';

async function fetchCapabilities() {
  const response = await fetch('/api/auth/capabilities');
  if (!response.ok) throw new Error(`Capabilities request failed: ${response.status}`);
  return authCapabilitiesSchema.parse(await response.json());
}

/**
 * The standing offer to a guest: register, and keep everything (PLAN.md
 * Phase 10.29).
 *
 * Shown on the circle's own page rather than as a banner on every screen. It
 * is an invitation, not a nag — someone deliberately chose not to have an
 * account, and interrupting their predictions to argue about it is how you
 * lose the person this feature exists for. Once here, though, it is explicit
 * about what is gained and what is kept, because "will I lose my picks?" is
 * the actual reason people don't take the offer.
 *
 * `callbackURL` is `/upgrade`, which is where the merge itself happens — see
 * `UpgradeScreen`, which runs at the one moment both credentials are present.
 */
export default function GuestUpgradePanel() {
  const { t } = useTranslation();

  const { data: capabilities } = useQuery({
    queryKey: ['auth-capabilities'],
    queryFn: fetchCapabilities,
    retry: false,
  });

  // Nothing to offer if the only sign-in method isn't configured — better an
  // absent panel than a button that fails.
  if (!capabilities?.google) return null;

  return (
    <section className={`panel stack ${styles.panel}`}>
      <h2 className={styles.title}>{t('guest.upgradeTitle')}</h2>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        {t('guest.upgradeHint')}
      </p>
      <ul className={styles.keeps}>
        <li>{t('guest.upgradeKeepsPicks')}</li>
        <li>{t('guest.upgradeKeepsPhoto')}</li>
        <li>{t('guest.upgradeGainsGroups')}</li>
      </ul>
      <GoogleButton
        label={t('guest.upgradeButton')}
        onClick={() =>
          void authClient.signIn.social({
            provider: 'google',
            callbackURL: `${window.location.origin}/upgrade`,
          })
        }
      />
    </section>
  );
}
